import { parse } from 'date-fns';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { Timeslot } from 'shared/types/timeslot';
import { isNumber } from 'shared/utils/is-number';
import { isValidTime } from 'shared/utils/is-valid-time';
import { parseDateTime } from 'shared/utils/parse-date-time';
import { processStringToHoursAndMinutes } from 'shared/utils/process-string-to-hours-and-minutes';

import * as Styled from './time-range-form.style';

export type TimeRangeFormProps = {
  initialValue?: Timeslot | null;
  timeFormat: TimeFormatOptions;
  onChange: (value: Timeslot | null) => void;
};

export const TimeRangeForm: React.FC<TimeRangeFormProps> = ({
  initialValue,
  timeFormat,
  onChange,
}) => {
  const { t } = useTranslation();
  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);
  const [startTime, setStartTime] = useState(
    isNumber(initialValue?.startTime)
      ? parseDateTime(initialValue!.startTime!, timeFormat)
      : '',
  );
  const [endTime, setEndTime] = useState(
    isNumber(initialValue?.duration)
      ? parseDateTime(
          (initialValue!.startTime ?? 0) + initialValue!.duration!,
          timeFormat,
        )
      : '',
  );

  const start = useMemo(() => {
    if (!isValidTime(startTime, timeFormat)) {
      return undefined;
    }

    const startDate = parse(startTime, timeFormat, new Date());
    return (startDate.getHours() * 60 + startDate.getMinutes()) * 60 * 1000;
  }, [startTime, timeFormat]);

  const duration = useMemo(() => {
    if (!isNumber(start) || !isValidTime(endTime, timeFormat)) {
      return undefined;
    }

    const endDate = parse(endTime, timeFormat, new Date());
    const end = (endDate.getHours() * 60 + endDate.getMinutes()) * 60 * 1000;
    return end - start!;
  }, [start, endTime, timeFormat]);

  const placeholder = t(
    timeFormat === TimeFormatOptions.Military
      ? 'forms.task.fields.startTime.military.placeholder'
      : 'forms.task.fields.startTime.standard.placeholder',
  );

  const handleStartTimeChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (!target.value.length) {
      setStartTime('');
      return;
    }

    // Process textual input based on the selected format
    const processedValue = processStringToHoursAndMinutes(
      target.value,
      timeFormat,
    );

    // Update the input
    setStartTime(processedValue);
  };
  const handleEndTimeChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (!target.value.length) {
      setEndTime('');
      return;
    }

    // Process textual input based on the selected format
    const processedValue = processStringToHoursAndMinutes(
      target.value,
      timeFormat,
    );

    // Update the input
    setEndTime(processedValue);
  };

  const onClear = () => onChange(null);
  const onSubmit = () => {
    if (isNumber(duration) && duration! >= 0) {
      onChange({ startTime: start, duration });
    }
  };

  useKeysDown(
    ['Enter'],
    (e) => {
      if (
        document.activeElement !== startRef.current &&
        document.activeElement !== endRef.current
      ) {
        return;
      }

      e.preventDefault();
      onSubmit();
    },
    { otherKeysInCombinationAllowed: true },
  );

  return (
    <Styled.TimeFormContainer>
      <Styled.TimeFormBody>
        <Styled.TimeInput
          ref={startRef}
          placeholder={placeholder}
          value={startTime}
          onChange={handleStartTimeChange}
        />
        <Styled.TimeSeparator>-</Styled.TimeSeparator>
        <Styled.TimeInput
          ref={endRef}
          placeholder={placeholder}
          value={endTime}
          onChange={handleEndTimeChange}
          $alignedRight
        />
      </Styled.TimeFormBody>

      <Styled.TimeFormFooter>
        <Button
          size={Sizes.Small}
          type="button"
          variant={Variants.Outlined}
          onClick={onClear}
          fullWidth
          alignCenter
        >
          {t('general.labels.clear')}
        </Button>
        <Button
          size={Sizes.Small}
          type="button"
          color={Colors.Primary}
          disabled={!isNumber(duration) || duration! < 0}
          onClick={onSubmit}
          fullWidth
          alignCenter
        >
          {t('general.labels.save')}
        </Button>
      </Styled.TimeFormFooter>
    </Styled.TimeFormContainer>
  );
};
